import React from 'react'
import PropTypes from 'prop-types'
import { PencilSimpleLine } from '@phosphor-icons/react'
import { BRAND_INDIGO, BRAND_WHITE, FONT } from './constants'

/* ---- circular "HUMAN-MADE" seal (solid, pencil) ----
 * Ring text sits on two semicircular paths — the top arc runs over the crown,
 * the bottom arc runs left-to-right under the base — so every glyph stays
 * upright. Both arcs share one text band; the centre holds the pencil, sized
 * against the inner circle so it never crosses that stroke.
 *
 * The centre once carried a "HUMAN-MADE" caption under the pencil, gated on it
 * reaching a legible ~6.2px. The inner chord only affords that from a seal of
 * ~110px up, and the seal renders at 88 (banner) and 68 (its own default), so
 * the caption never appeared. Restoring it means loosening that legibility gate
 * — a design call — not re-adding the branch.
 *
 * The ring micro-text is a fixed brand stamp and is not localised; when the seal
 * carries meaning on its own, pass a localised `ariaLabel`. Inside the banner
 * the adjacent statement conveys the meaning, so it is rendered `decorative`
 * (hidden from assistive tech) to avoid a duplicate announcement.
 */
const HumanMadeSeal = ({
  size = 68,
  color = BRAND_INDIGO,
  top = 'RESEARCHED · WRITTEN',
  bottom = 'REVIEWED BY HUMANS',
  ariaLabel,
  decorative = false
}) => {
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, '')
  const c = size / 2

  /* ring band */
  const pad = Math.max(6, size * 0.075)
  const rBand = c - pad
  const maxChars = Math.max(top.length, bottom.length)
  const fsRing = Math.min(size * 0.078, (Math.PI * rBand * 0.8) / (maxChars * 0.78))
  const capRing = fsRing * 0.72
  const track = fsRing * 0.12
  const rTop = rBand - capRing // top baseline: glyphs grow outwards
  const rBot = rBand // bottom baseline: glyphs grow inwards
  const rInner = rTop - Math.max(7, size * 0.062)
  const rDot = rBand - capRing / 2

  /* centre — the pencil fills the inner circle */
  const iconSize = Math.round(rInner * 1.1)
  const ringFg = 'rgba(255,255,255,.85)'

  const a11y = decorative
    ? { 'aria-hidden': 'true' }
    : { role: 'img', 'aria-label': ariaLabel ?? `${top} ${bottom}` }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }} {...a11y}>
      <defs>
        <path id={`${uid}-t`} fill='none' d={`M ${c - rTop} ${c} A ${rTop} ${rTop} 0 0 1 ${c + rTop} ${c}`} />
        <path id={`${uid}-b`} fill='none' d={`M ${c - rBot} ${c} A ${rBot} ${rBot} 0 0 0 ${c + rBot} ${c}`} />
      </defs>
      <circle cx={c} cy={c} r={c - 1.25} fill={color} />
      <g fontFamily={FONT} fontSize={fsRing} fontWeight='700' letterSpacing={track} fill={ringFg}>
        <text textAnchor='middle'><textPath href={`#${uid}-t`} startOffset={(Math.PI * rTop) / 2 + track / 2}>{top}</textPath></text>
        <text textAnchor='middle'><textPath href={`#${uid}-b`} startOffset={(Math.PI * rBot) / 2 + track / 2}>{bottom}</textPath></text>
      </g>
      <circle cx={c - rDot} cy={c} r={fsRing * 0.15} fill={ringFg} />
      <circle cx={c + rDot} cy={c} r={fsRing * 0.15} fill={ringFg} />
      <circle cx={c} cy={c} r={rInner} fill='none' stroke='rgba(255,255,255,.4)' strokeWidth='1' />
      <g transform={`translate(${c - iconSize / 2}, ${c - iconSize / 2})`} style={{ color: BRAND_WHITE }}>
        <PencilSimpleLine size={iconSize} weight='bold' />
      </g>
    </svg>
  )
}

HumanMadeSeal.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  top: PropTypes.string,
  bottom: PropTypes.string,
  ariaLabel: PropTypes.string,
  decorative: PropTypes.bool
}

export default HumanMadeSeal
